import type {
  EnrichedEditorDocument,
  EnrichedQueuedUpload,
  DraftMessageAttachment,
} from "@/payroll/editor/types";

export interface Attachment {
  id: string;
  clientUploadId?: string | null;
  submitted?: boolean;
  documentId?: string;
  uploadId?: string;
  name: string;
  error?: string | null;
  notUsed?: boolean;
  file?: File;
  previewPath?: string | null;
  contentType?: string | null;
  size?: number;
  canRetry?: boolean;
}

export const enrichAttachments = (
  documents: EnrichedEditorDocument[],
  uploads: EnrichedQueuedUpload[],
  localFiles: Record<string, File>,
): Attachment[] => {
  const saved = documents.map((document) => {
    const upload = uploads.find(
      (item) =>
        item.clientId === document.client_upload_id ||
        item.documentId === document.id,
    );
    return {
      id: document.client_upload_id ?? document.id,
      clientUploadId: document.client_upload_id,
      submitted: document.submitted ?? true,
      documentId: document.id,
      uploadId: upload?.clientId,
      name: document.title,
      error: upload?.error ?? document.errorMessage,
      notUsed: document.status === "extraction_failed",
      file: upload?.file ?? localFiles[document.client_upload_id ?? ""],
      previewPath: document.preview_path,
      contentType: document.content_type,
      size: document.file_size,
      canRetry: upload?.canRetry,
    };
  });
  return [
    ...saved,
    ...uploads
      .filter(
        (upload) =>
          !saved.some(
            (item) =>
              item.id === upload.clientId ||
              item.documentId === upload.documentId,
          ),
      )
      .map((upload) => ({
        id: upload.clientId,
        clientUploadId: upload.clientId,
        submitted: false,
        uploadId: upload.clientId,
        name: upload.name,
        error: upload.error,
        file: upload.file,
        contentType: upload.file?.type,
        size: upload.file?.size,
        canRetry: upload.canRetry,
      })),
  ];
};

export const draftMessageAttachments = (
  files: Attachment[],
  ids: string[],
): DraftMessageAttachment[] =>
  files
    .filter((file) => ids.includes(file.id))
    .map((file) => ({
      id: file.documentId ?? file.id,
      client_upload_id: file.clientUploadId,
      title: file.name,
      file_name: file.name,
      content_type: file.contentType,
      file_size: file.size,
      preview_path: file.previewPath,
      not_used: file.notUsed || undefined,
      error: file.error,
      file: file.file,
    }));

export const messageAttachment = (
  file: DraftMessageAttachment,
): Attachment => ({
  id: file.id,
  name: file.title,
  contentType: file.content_type,
  size: file.file_size,
  previewPath: file.preview_path,
  notUsed: file.not_used,
  error: file.error,
  file: file.file,
});

export const isImageAttachment = (file: Attachment) =>
  !!file.previewPath ||
  ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(
    file.contentType ?? "",
  );
